/**
 * The two provider marks, drawn exactly as their owners publish them.
 *
 * These are the only marks in the product that are not ours, and the only place a colour is
 * written that is neither a token nor a network colour from `shared/src/providers.ts`. Both
 * companies' guidelines are prescriptive about the logo — its colours, its proportions and its
 * minimum size — and `docs/DESIGN_SYSTEM.md` § the sign-in card is where our system yields.
 *
 * Rules that must survive any edit:
 *   • the Google "G" keeps all four colours on every background. There is no mono version.
 *   • the Facebook "f" is white on Facebook Blue, or Facebook Blue on white. Never recoloured.
 *   • neither mark is ever rotated, outlined, given a shadow, or set below 18 px.
 *   • `aria-hidden`, always: the button's own text already names the provider, and a screen
 *     reader announcing "Google logo Continue with Google" says it twice.
 */

import type { ReactElement } from 'react'

export interface MarkProps {
  /** Google's minimum is 18 px. Facebook's clear space is derived from the same figure. */
  size?: number
}

/**
 * Google's "G", the four-colour version, from the Sign in with Google asset set.
 *
 * The same mark serves the light and the dark button — Google publishes no dark-mode variant of
 * the G itself, only of the button around it.
 */
export const GoogleMark = ({ size = 18 }: MarkProps): ReactElement => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 48 48"
    aria-hidden="true"
    focusable="false"
    style={{ display: 'block', flexShrink: 0 }}
  >
    <path
      fill="#4285F4"
      d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
    />
    <path
      fill="#34A853"
      d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
    />
    <path
      fill="#FBBC05"
      d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"
    />
    <path
      fill="#EA4335"
      d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
    />
  </svg>
)

export interface FacebookMarkProps extends MarkProps {
  /** `#FFFFFF` on the blue button, Facebook Blue on the white one. Nothing else. */
  colour: string
}

/**
 * The Facebook "f" letterform, sized by height the way Meta's assets are.
 *
 * The glyph is taller than it is wide, so `size` is its height and the width follows the
 * mark's own proportions rather than being squared off.
 */
export const FacebookMark = ({ size = 20, colour }: FacebookMarkProps): ReactElement => (
  <svg
    height={size}
    width={(size * 320) / 512}
    viewBox="0 0 320 512"
    aria-hidden="true"
    focusable="false"
    style={{ display: 'block', flexShrink: 0 }}
  >
    <path
      fill={colour}
      d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z"
    />
  </svg>
)
